import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';

export interface UserPoints {
  points: number;
}

/**
 * Get user's current points
 * @param userId - The user's ID
 * @returns Promise<UserPoints | null>
 */
export const getUserPoints = async (userId: string): Promise<UserPoints | null> => {
  try {
    const pointsRef = doc(db, 'points', userId);
    const pointsDoc = await getDoc(pointsRef);
    
    if (pointsDoc.exists()) {
      const data = pointsDoc.data() as UserPoints;
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching user points:', error);
    throw error;
  }
};

/**
 * Get user's points or return 0 if no points document exists
 * @param userId - The user's ID
 * @returns Promise<number>
 */
export const getUserPointsCount = async (userId: string): Promise<number> => {
  try {
    const pointsData = await getUserPoints(userId);
    console.log('pointsData', pointsData);
    return pointsData?.points || 0;
  } catch (error) {
    console.error('Error fetching user points count:', error);
    return 0;
  }
};

/**
 * Test function to manually check points in database
 */
export const testUserPoints = async (userId: string) => {
  try {
    console.log('Testing direct database access for user:', userId);
    const pointsRef = doc(db, 'points', userId);
    const pointsDoc = await getDoc(pointsRef);
    
    console.log('Document exists:', pointsDoc.exists());
    console.log('Document data:', pointsDoc.data());
    
    if (pointsDoc.exists()) {
      const data = pointsDoc.data();
      console.log('Points value:', data.points);
      return data.points;
    } else {
      // console.log('No points document found for user');
      return 0;
    }
  } catch (error) {
    console.error('Error in testUserPoints:', error);
    return 0;
  }
};

/**
 * React hook to manage user points with refresh capability
 * @returns Hook with points data and refresh function
 */
export const useUserPoints = () => {
  const { currentUser } = useAuth();
  const [points, setPoints] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Function to fetch points directly from Firestore
  const fetchPoints = useCallback(async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching points for user:', currentUser.uid);
      
      const pointsRef = doc(db, 'points', currentUser.uid);
      const pointsDoc = await getDoc(pointsRef);
      
      if (pointsDoc.exists()) {
        const data = pointsDoc.data() as UserPoints;
        console.log('Points data from Firestore:', data);
        setPoints(data.points || 0);
      } else {
        // console.log('No points document found, setting to 0');
        setPoints(0);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching points:', err);
      setError(err as Error);
      setPoints(0);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Set up real-time listener
  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    // console.log('Setting up real-time listener for user:', currentUser.uid);
    const pointsRef = doc(db, 'points', currentUser.uid);
    
    const unsubscribe = onSnapshot(pointsRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data() as UserPoints;
        console.log('Real-time points update:', data);
        setPoints(data.points || 0);
      } else {
        // console.log('Real-time: No points document found');
        setPoints(0);
      }
      setLoading(false);
    }, (error) => {
      console.error('Real-time listener error:', error);
      setError(error);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser?.uid]);

  // Refresh function
  const refreshPoints = useCallback(async () => {
    console.log('Manual refresh requested');
    await fetchPoints();
  }, [fetchPoints]);

  // Debug logging removed to prevent excessive console output

  return {
    points,
    loading,
    error,
    refreshPoints,
  };
}; 